/**
 * A aba Lançamentos: a barra de lote, os filtros e a tabela do caixa.
 *
 * Papel: reproduz o `#tab-lancamentos` da origem (l. 333–461) e o que
 * `renderLancamentos`, as funções de lote e as da linha fazem (l. 2491–2512,
 * 2869–2931): guarda os filtros e a seleção, pede a listagem, e grava o lote,
 * o ✓, o selo de situação e o 🗑, com as perguntas e os avisos da origem.
 *
 * Como usar: o mês aberto vem da página e só vale sem pesquisa e sem período.
 * A Pesquisa espera 300 ms sem tecla antes de pedir; os outros filtros pedem
 * na hora, e a tabela mantém o resultado anterior enquanto o novo não chega.
 * Os filtros e a seleção ficam aqui, e não na página, porque o painel desta
 * aba fica montado o tempo todo: trocar de aba e voltar não os perde.
 *
 * A seleção guarda a linha inteira no momento em que é marcada, e por isso
 * sobrevive à troca de filtro — a barra de lote soma linhas que a tabela
 * deixou de mostrar, como a origem. A linha que a listagem atual traz vence o
 * retrato guardado, e a barra soma o valor depois de uma edição; o 🗑 da linha
 * e o do modal tiram o lançamento da seleção. Toda ação de lote vale para os pagos
 * também, limpa a seleção e avisa; a exclusão pergunta antes. O 🧾 e os botões
 * de importação e da calculadora são da parte 5, e chegam pelas props.
 *
 * "Novo Lançamento" e o ✎ da linha abrem o `ModalDeLancamento`, que só chega
 * à tela depois do plano de contas, dos favorecidos e dos padrões: do plano sai
 * a primeira categoria do tipo, e dos outros dois, o realce que o ✎ calcula uma
 * vez só, ao abrir.
 *
 * "Exportar XLSX" leva as linhas à vista, na ordem da tabela, e o aviso diz o
 * período dos filtros; sem linha, avisa que não há o que exportar. Enquanto a
 * tabela mostra o resultado do filtro anterior — a nova busca não voltou, ou a
 * Pesquisa ainda espera os 300 ms —, recusa com "Aguarde a lista carregar".
 *
 * Arrastar uma linha pela alça e soltá-la sobre outra do **mesmo mês** grava a
 * ordem nova, que liga a ordem manual daquele mês; soltar numa linha de outro
 * mês não faz nada, como `reorderLanc` (l. 2711).
 */
import { type AcaoDoLote, type LancamentoDaListagem, acessoAlcanca } from '@concierge/shared';
import { useRef, useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { useAcesso } from '@/shared/hooks';
import { exportarPlanilha } from '@/shared/lib/planilha';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  FILTROS_VAZIOS,
  type FiltrosDosLancamentos,
  filtroDaConsulta,
  filtrosDoLimpar,
} from '../filtros-dos-lancamentos';
import {
  useCasaDoCaixa,
  useExcluirLancamento,
  useFavorecidos,
  useLancamentos,
  useLoteDeLancamentos,
  usePadroesDeFavorecido,
  usePlanoDeContas,
  useReordenarLancamento,
} from '../hooks/usar-financas';
import { useArrasteDeLinhas } from '../hooks/use-arraste-de-linhas';
import { useValorAtrasado } from '../hooks/use-valor-atrasado';
import { avisoDaExportacao, planilhaDosLancamentos } from '../planilhas';
import { BarraDeLote } from './barra-de-lote';
import { CartaoDeFiltros } from './cartao-de-filtros';
import { ModalDeImportacao } from './modal-de-importacao';
import { ModalDeLancamento } from './modal-de-lancamento';
import { TabelaDeLancamentos } from './tabela-de-lancamentos';

/** Quanto tempo a Pesquisa espera sem tecla antes de pedir. */
const ESPERA_DA_PESQUISA_EM_MS = 300;

/** O aviso de cada ação de lote, menos a exclusão, que conta (l. 2903–2931). */
const AVISO_DO_LOTE: Readonly<Record<Exclude<AcaoDoLote, 'excluir'>, string>> = {
  marcar_pago: '✓ Marcados como pagos',
  reverter_pendente: 'Revertidos para pendente',
  marcar_lancado: '🏦 Marcados como Lançado',
  marcar_provisionado: '📋 Marcados como Provisionado',
};

/**
 * O plural da origem: o "s" quando são mais de um.
 *
 * @param quantos - Quantos são.
 * @returns "s" ou vazio.
 */
function plural(quantos: number): string {
  return quantos > 1 ? 's' : '';
}

/**
 * O texto de uma falha de gravação, para o aviso vermelho.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem do erro, que vem da API e nunca traz dado da família.
 */
function mensagemDaFalha(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

interface AbaLancamentosProps {
  /** O mês aberto da página, em `AAAA-MM`. */
  mesAberto: string;
  /** A opção "Folha (PDF)" do modal Importar, que abre a seleção do PDF da folha. */
  onImportarFolhaPdf?: () => void;
  /** A opção "Guia DAE (PDF)" do modal Importar, que abre a seleção do PDF da guia. */
  onImportarGuiaDae?: () => void;
  /** A "Calculadora Petrella", da parte 5. */
  onAbrirCalculadora?: () => void;
  /** O 🧾 da linha, da parte 5. */
  onImprimirRecibo?: (linha: LancamentoDaListagem) => void;
}

/**
 * A aba Lançamentos.
 *
 * @param mesAberto - O mês aberto da página.
 * @param onImportarFolhaPdf - A opção "Folha (PDF)" do modal Importar.
 * @param onImportarGuiaDae - A opção "Guia DAE (PDF)" do modal Importar.
 * @param onAbrirCalculadora - A "Calculadora Petrella".
 * @param onImprimirRecibo - O 🧾 da linha.
 * @returns A barra de lote, o cartão de filtros e a tabela.
 */
export function AbaLancamentos({
  mesAberto,
  onImportarFolhaPdf,
  onImportarGuiaDae,
  onAbrirCalculadora,
  onImprimirRecibo,
}: AbaLancamentosProps) {
  const casa = useCasaDoCaixa();
  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'financas', 'editar');
  const notificar = useNotificacao();
  const plano = usePlanoDeContas();
  const lote = useLoteDeLancamentos();
  const excluir = useExcluirLancamento();
  const reordenar = useReordenarLancamento();
  const favorecidos = useFavorecidos(casa.residenciaId);
  const padroes = usePadroesDeFavorecido();

  const [filtros, setFiltros] = useState<FiltrosDosLancamentos>(FILTROS_VAZIOS);
  const [selecionados, setSelecionados] = useState<ReadonlyMap<string, LancamentoDaListagem>>(
    () => new Map(),
  );
  const [modal, setModal] = useState<{ editado: LancamentoDaListagem | null } | null>(null);
  const [importando, setImportando] = useState(false);
  const aplicandoLote = useRef(false);
  const pesquisa = useValorAtrasado(filtros.pesquisa, ESPERA_DA_PESQUISA_EM_MS);

  const consulta =
    casa.residenciaId === null
      ? null
      : filtroDaConsulta(casa.residenciaId, mesAberto, { ...filtros, pesquisa });
  const listagem = useLancamentos(consulta);

  const linhas = listagem.data?.lancamentos ?? [];
  const linhaDaListagemPorId = new Map(linhas.map((linha) => [linha.id, linha]));
  let situacao: 'pronta' | 'carregando' | 'falhou' = 'pronta';
  let tentarDeNovo: () => void = () => void listagem.refetch();
  if (casa.situacao !== 'pronta') {
    situacao = casa.situacao;
    tentarDeNovo = casa.tentarDeNovo;
  } else if (consulta !== null && listagem.data === undefined) {
    situacao = listagem.isError ? 'falhou' : 'carregando';
  }

  const categorias = plano.data?.map((categoria) => categoria.nome) ?? [];
  const coresDasCategorias = new Map(
    plano.data?.map((categoria) => [categoria.nome, categoria.cor]) ?? [],
  );

  /**
   * Marca ou desmarca uma linha, guardando-a inteira.
   *
   * @param linha - A linha da caixa clicada.
   * @param marcada - A nova marcação.
   */
  function handleMarcar(linha: LancamentoDaListagem, marcada: boolean) {
    setSelecionados((anteriores) => {
      const proximos = new Map(anteriores);
      if (marcada) proximos.set(linha.id, linha);
      else proximos.delete(linha.id);
      return proximos;
    });
  }

  /**
   * Tira da seleção o lançamento excluído, para a barra de lote não somar uma
   * linha que já não existe.
   *
   * @param id - O lançamento excluído.
   */
  function tirarDaSelecao(id: string) {
    setSelecionados((anteriores) => {
      if (!anteriores.has(id)) return anteriores;
      const proximos = new Map(anteriores);
      proximos.delete(id);
      return proximos;
    });
  }

  /**
   * Marca ou desmarca as linhas à vista, pela caixa do cabeçalho.
   *
   * @param marcadas - A nova marcação.
   */
  function handleMarcarVisiveis(marcadas: boolean) {
    setSelecionados((anteriores) => {
      const proximos = new Map(anteriores);
      for (const linha of linhas) {
        if (marcadas) proximos.set(linha.id, linha);
        else proximos.delete(linha.id);
      }
      return proximos;
    });
  }

  /**
   * Aplica uma ação de lote aos selecionados, pagos inclusive.
   *
   * Um segundo clique enquanto o lote não volta não faz nada: o `isPending`
   * só desabilita a barra no próximo desenho, e sem a trava a exclusão
   * perguntaria de novo e mandaria os mesmos ids.
   *
   * @param acao - A ação da barra.
   */
  async function handleAcaoDoLote(acao: AcaoDoLote) {
    if (aplicandoLote.current) return;
    const ids = [...selecionados.keys()];
    const quantos = ids.length;
    if (
      acao === 'excluir' &&
      !window.confirm(
        `Excluir ${String(quantos)} lançamento${plural(quantos)} selecionado${plural(quantos)}? Essa ação não pode ser desfeita.`,
      )
    ) {
      return;
    }
    aplicandoLote.current = true;
    try {
      const { alterados } = await lote.mutateAsync({ acao, ids });
      setSelecionados(new Map());
      notificar(
        acao === 'excluir'
          ? `✓ ${String(alterados)} lançamento${plural(alterados)} excluído${plural(alterados)}`
          : AVISO_DO_LOTE[acao],
      );
    } catch (erro) {
      notificar(mensagemDaFalha(erro), false);
    } finally {
      aplicandoLote.current = false;
    }
  }

  /**
   * Grava a ação de uma linha só, pelo mesmo lote.
   *
   * @param acao - `marcar_pago`, `marcar_lancado` ou `marcar_provisionado`.
   * @param linha - A linha.
   * @param aviso - O que avisar quando der certo.
   */
  async function handleAcaoDaLinha(
    acao: 'marcar_pago' | 'marcar_lancado' | 'marcar_provisionado',
    linha: LancamentoDaListagem,
    aviso: string,
  ) {
    try {
      const { alterados } = await lote.mutateAsync({ acao, ids: [linha.id] });
      // O lote não recusa o id que não existe; zero alterados é o lançamento
      // que outra pessoa excluiu enquanto a lista estava na tela.
      if (alterados === 0) {
        notificar('Lançamento não encontrado', false);
        return;
      }
      notificar(aviso);
    } catch (erro) {
      notificar(mensagemDaFalha(erro), false);
    }
  }

  /**
   * Troca a provisão do pendente, pelo clique no selo (l. 2491–2496).
   *
   * @param linha - A linha do selo.
   */
  function handleAlternarProvisao(linha: LancamentoDaListagem) {
    if (linha.provisao) {
      void handleAcaoDaLinha('marcar_lancado', linha, '🏦 Marcado como Lançado');
    } else {
      void handleAcaoDaLinha('marcar_provisionado', linha, '📋 Marcado como Provisionado');
    }
  }

  /**
   * Exclui a linha pelo 🗑, depois de perguntar.
   *
   * @param linha - A linha.
   */
  async function handleExcluir(linha: LancamentoDaListagem) {
    if (!window.confirm('Excluir este lançamento?')) return;
    try {
      await excluir.mutateAsync(linha.id);
      tirarDaSelecao(linha.id);
      notificar('Lançamento excluído');
    } catch (erro) {
      notificar(mensagemDaFalha(erro), false);
    }
  }

  /**
   * Grava a linha solta antes ou depois de outra do mesmo mês.
   *
   * @param origemId - A linha arrastada.
   * @param alvoId - A linha sobre a qual ela foi solta.
   * @param depois - Se foi solta na metade de baixo.
   */
  async function handleSoltar(origemId: string, alvoId: string, depois: boolean) {
    const origem = linhas.find((linha) => linha.id === origemId);
    const alvo = linhas.find((linha) => linha.id === alvoId);
    if (origem === undefined || alvo === undefined) return;
    if (origem.data.slice(0, 7) !== alvo.data.slice(0, 7)) return;
    try {
      await reordenar.mutateAsync({ id: origemId, alvoId, depois });
      notificar('✓ Ordem atualizada');
    } catch (erro) {
      notificar(mensagemDaFalha(erro), false);
    }
  }

  const arraste = useArrasteDeLinhas((origemId, alvoId, depois) => {
    void handleSoltar(origemId, alvoId, depois);
  });

  /** Exporta as linhas à vista para o Excel (l. 2807–2839). */
  async function handleExportar() {
    if (listagem.isPlaceholderData || pesquisa !== filtros.pesquisa) {
      notificar('Aguarde a lista carregar', false);
      return;
    }
    if (linhas.length === 0) {
      notificar('Nenhum lançamento para exportar', false);
      return;
    }
    try {
      await exportarPlanilha(planilhaDosLancamentos(linhas, filtros, hojeEmIso()));
      notificar(avisoDaExportacao(linhas.length, filtros));
    } catch {
      notificar(
        'A planilha não pôde ser gerada. Verifique a conexão com a internet e tente novamente.',
        false,
      );
    }
  }

  /** O "✕ Limpar": os filtros da origem e a seleção vazia. */
  function handleLimpar() {
    setFiltros(filtrosDoLimpar(hojeEmIso()));
    setSelecionados(new Map());
  }

  return (
    <div>
      <BarraDeLote
        selecionados={[...selecionados.values()].map(
          (retrato) => linhaDaListagemPorId.get(retrato.id) ?? retrato,
        )}
        podeEditar={podeEditar}
        ocupado={lote.isPending}
        onAcao={(acao) => void handleAcaoDoLote(acao)}
        onCancelar={() => setSelecionados(new Map())}
      />
      <CartaoDeFiltros
        saldoAtual={casa.saldoAtual}
        filtros={filtros}
        onMudar={(mudanca) => setFiltros((anteriores) => ({ ...anteriores, ...mudanca }))}
        onLimpar={handleLimpar}
        categorias={categorias}
        podeEditar={podeEditar}
        onNovo={() => setModal({ editado: null })}
        onExportar={() => void handleExportar()}
        onImportar={() => setImportando(true)}
        onAbrirCalculadora={onAbrirCalculadora}
      />
      <TabelaDeLancamentos
        linhas={linhas}
        soma={listagem.data?.soma ?? 0}
        situacao={situacao}
        onTentarDeNovo={tentarDeNovo}
        selecionados={selecionados}
        onMarcar={handleMarcar}
        onMarcarVisiveis={handleMarcarVisiveis}
        coresDasCategorias={coresDasCategorias}
        podeEditar={podeEditar}
        onEditar={(linha) => setModal({ editado: linha })}
        onConfirmarPago={(linha) =>
          void handleAcaoDaLinha('marcar_pago', linha, '✓ Marcado como pago')
        }
        onExcluir={(linha) => void handleExcluir(linha)}
        onAlternarProvisao={handleAlternarProvisao}
        onImprimirRecibo={onImprimirRecibo}
        onAgarrar={arraste.handleAgarrar}
        idArrastado={arraste.idArrastado}
        idSobre={arraste.idSobre}
      />
      {modal !== null &&
        casa.residenciaId !== null &&
        plano.data !== undefined &&
        favorecidos.data !== undefined &&
        padroes.data !== undefined && (
          <ModalDeLancamento
            editado={modal.editado}
            residenciaId={casa.residenciaId}
            plano={plano.data}
            favorecidos={favorecidos.data}
            padroes={padroes.data}
            onFechar={() => setModal(null)}
            onExcluido={tirarDaSelecao}
          />
        )}
      {importando && plano.data !== undefined && (
        <ModalDeImportacao
          plano={plano.data}
          lancamentos={linhas}
          onFolha={() => onImportarFolhaPdf?.()}
          onGuia={() => onImportarGuiaDae?.()}
          onFechar={() => setImportando(false)}
        />
      )}
      {arraste.fantasma !== null && (
        <div
          className="pointer-events-none fixed z-[9999] rounded-[6px] border-2 border-emb-fantasma-borda bg-superficie px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap text-emb-fantasma-texto shadow-emb-fantasma"
          style={{ left: arraste.fantasma.x, top: arraste.fantasma.y }}
        >
          {arraste.fantasma.texto}
        </div>
      )}
    </div>
  );
}
